<template>
	<div class="w-100 form-container no-outline" :id="elementId">
		<component :is="component" :modelValue="localModelValue" @update:modelValue="setValue" :options="options" :data="data" ref="comp" v-bind="componentProps"></component>
	</div>
</template>

<script lang="ts">
	import { Component, defineComponent, markRaw } from 'vue'
	import isEqual from 'lodash/isEqual'
	import merge from 'lodash/merge'
	import { isFunction } from 'lodash'
	import { IExtendedGridApi, IValidator } from '../interfaces'
	import { GridOptions, Column, GridApi, IRowNode } from 'ag-grid-enterprise'
	import cloneDeep from 'lodash/cloneDeep'

	export default defineComponent({
		name: "custom-generic",
		props: ['params'],
		created() {
			if (this.params) {
				const { modelValue, options, component, data, getterConverter, componentProps } = this.params.generateProps(this.params)
				this.component = markRaw(component)
				this.getterConverter = getterConverter
				this.data = data
				this.column = this.params.column
				this.options = options
				this.componentProps = componentProps
				this.setValue(modelValue)
				this.rowData = this.params.node.data
				this.gridApi = this.params.api as IExtendedGridApi
				this.leaving = this.params.colDef.leaving
				this.elementId = `${this.rowData.displayOrder}__${this.params.colDef.field}__${new Date().getTime()}`

				if (this.params.options) {
					if (isFunction(this.params.options))
						this.options = merge(this.options, this.params.options(this.params))

					else
						this.options = merge(this.options, this.params.options)
				}
			}
		},
		mounted() {
			this.$nextTick(() => {
				this.focusOnComponent()
			})
		},
		beforeUnmount() {
			// this.$keyHandler.Unbind(document.getElementById(`${this.elementId}`) as Element)
		},
		data() {
			return {
				elementId: '',
				options: {},
				component: 'nk-input' as string | Component,
				getterConverter: null as null | Function,
				localModelValue: null,
				originalValue: null,
				rowData: null as any,
				data: [],
				gridApi: null as IExtendedGridApi | null,
				column: null as Column | null,
				leaving: null as any,
				validators: [] as IValidator[],
				componentProps: {}
			}
		},
		methods: {
			dataUpdated(data: any) {
			},
			getValue() {
				return typeof this.getterConverter === 'function' ? this.getterConverter(this.localModelValue) : this.localModelValue
			},
			setValue(val: any) {
				if (!isEqual(this.originalValue, val)) {
					this.localModelValue = cloneDeep(val)
					this.originalValue = cloneDeep(val)

					if (this.rowData)
						this.rowData.rowState = !this.rowData.rowState || this.rowData.rowState === 'unchanged' ? 'changed' : this.rowData.rowState
				}
			},
			getValueSetterProperties(rowData: any) {
				const node = this.params.node
				node.data = rowData
				return {
					oldValue: rowData[this.params.colDef.field as string],
					newValue: this.getValue(),
					node: node,
					data: rowData,
					column: this.column,
					colDef: this.params.colDef,
					api: this.gridApi,
					context: null
				}
			},
			getNewRowData() {
				const newRowData = cloneDeep(this.rowData)

				if (this.params.colDef.valueSetter)
					this.params.colDef.valueSetter(this.getValueSetterProperties(newRowData))
				else
					newRowData[this.params.colDef.field as string] = this.getValue()
				return newRowData
			},
			handleCellLeaving() {
				let canLeave = false

				if (!this.leaving && this.leaving !== false)
					canLeave = true

				else if (typeof this.leaving === 'function') {
					const newRowData = this.getNewRowData()
					canLeave = this.leaving(newRowData, this.column)
				}

				else if (typeof this.leaving === 'boolean')
					canLeave = this.leaving

				return canLeave
			},
			isRowValid() {
				if (!this.gridApi) return false

				if (this.gridApi.validateRowData) {
					const newRowData = this.getNewRowData()

					if (!this.gridApi.validateRowData(newRowData, true, this.rowData).isValid)
						return false
				}

				return true
			},
			navigateEditableCell(gridApi: IExtendedGridApi, direction: 'up' | 'down' | 'left' | 'right') {
				if (!gridApi || !this.handleCellLeaving()) return

				if (direction === 'up' || direction === 'down') {
					this.navigateVertically(gridApi, direction)
				} else if (direction === 'left' || direction === 'right') {
					this.navigateHorizontally(gridApi, direction)
				}
			},
			navigateVertically(gridApi: IExtendedGridApi, direction: 'up' | 'down') {
				if (!this.gridApi) return

				const { column, rowIndex } = gridApi.getEditingCells()[0]
				const rowCount = gridApi.getDisplayedRowCount()
				let newRowIndex = direction === 'up' ? rowIndex - 1 : rowIndex + 1
				let node: IRowNode | null = null

				if (!(direction === 'up' && (rowCount - newRowIndex === 2) && gridApi.tryDeleteLastEmptyRow && gridApi.tryDeleteLastEmptyRow()) && !this.isRowValid()) return

				// Wrap around if we reach beyond the limits
				if (newRowIndex < 0) {
					return
				} else if (newRowIndex >= rowCount) {
					const canAddRow = gridApi.getGridOption('canAddRow' as keyof GridOptions)

					if (canAddRow && gridApi.addNewRow) {
						gridApi.addNewRow('end', true)
						return
					}
				}

				let foundEditableCell = false
				gridApi.stopEditing()

				// Loop until we find an editable cell or wrap back to the original row
				while (!foundEditableCell) {
					const rowNode = gridApi.getDisplayedRowAtIndex(newRowIndex)

					if (rowNode) {
						const firstEditableCol = gridApi.getColumns()?.find(col => col.isCellEditable && col.isCellEditable(rowNode))
						if (column.isCellEditable(rowNode)) {
							node = rowNode
							foundEditableCell = true
						}
						else if (firstEditableCol) {
							gridApi.startEditingCell({
								rowIndex: newRowIndex,
								colKey: firstEditableCol.getId(),
							})
							rowNode.setSelected(true, true)
							return
						}
						else {
							newRowIndex = direction === 'up' ? newRowIndex - 1 : newRowIndex + 1
							if (newRowIndex < 0) newRowIndex = rowCount - 1
							else if (newRowIndex >= rowCount) newRowIndex = 0
							if (newRowIndex === rowIndex) break
						}
					}
					else {
						newRowIndex = direction === 'up' ? newRowIndex - 1 : newRowIndex + 1
						if (newRowIndex < 0) newRowIndex = rowCount - 1
						else if (newRowIndex >= rowCount) newRowIndex = 0
						if (newRowIndex === rowIndex) break
					}
				}

				if (foundEditableCell) {
					gridApi.stopEditing()
					gridApi.startEditingCell({
						rowIndex: newRowIndex,
						colKey: column.getId(),
					})
					node?.setSelected(true, true)
				}
			},
			navigateHorizontally(gridApi: IExtendedGridApi, direction: 'left' | 'right') {
				const { column, rowIndex } = gridApi.getEditingCells()[0]
				// Get only visible columns to avoid navigating to hidden columns
				const allColumns = gridApi.getAllGridColumns().filter(col => col.isVisible())
				let newColumnIndex = allColumns.findIndex(col => col.getId() === column.getId())
				let newRowIndex = rowIndex
				let node: IRowNode | null = null

				// If current column is not in visible columns, find the first visible editable column
				if (newColumnIndex === -1) {
					const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex)
					if (rowNode) {
						const firstVisibleEditable = allColumns.find(col => col.isCellEditable(rowNode))
						if (firstVisibleEditable) {
							newColumnIndex = allColumns.findIndex(col => col.getId() === firstVisibleEditable.getId())
						} else {
							return // No visible editable columns found
						}
					} else {
						return // No row node found
					}
				}

				if (direction === 'left') {
					newColumnIndex += 1

					const currentRowNode = gridApi.getDisplayedRowAtIndex(rowIndex)
					const hasMoreEditableInRow = currentRowNode && allColumns.slice(newColumnIndex).some(col => col.isCellEditable(currentRowNode))

					if (!hasMoreEditableInRow) {
						newColumnIndex = 0
						newRowIndex += 1

						if (newRowIndex >= gridApi.getDisplayedRowCount()) {
							if (!this.isRowValid() || !gridApi.getGridOption('canAddRow' as keyof GridOptions)) return
							if (gridApi.addNewRow) gridApi.addNewRow('end', false)
							newRowIndex = gridApi.getDisplayedRowCount() - 1
						}
					}
				} else if (direction === 'right') {
					newColumnIndex -= 1

					const currentRowNode = gridApi.getDisplayedRowAtIndex(rowIndex)
					const hasMoreEditableInRow = currentRowNode && allColumns.slice(0, newColumnIndex + 1).some(col => col.isCellEditable(currentRowNode))

					if (!hasMoreEditableInRow) {
						newColumnIndex = allColumns.length - 1
						newRowIndex -= 1

						if (newRowIndex < 0) {
							newRowIndex = gridApi.getDisplayedRowCount() - 1
						}
					}
				}

				let foundEditableCell = false
				gridApi.stopEditing()

				// Search for an editable cell in the specified row
				const originalColumnIndex = allColumns.findIndex(col => col.getId() === column.getId())
				while (!foundEditableCell) {
					const rowNode = gridApi.getDisplayedRowAtIndex(newRowIndex)
					const targetColumn = allColumns[newColumnIndex]

					if (rowNode && targetColumn && targetColumn.isCellEditable(rowNode)) {
						foundEditableCell = true
						node = rowNode
					} else {
						newColumnIndex = direction === 'left' ? newColumnIndex + 1 : newColumnIndex - 1

						// Wrap within the row's columns if no editable cell is found
						if (newColumnIndex < 0) newColumnIndex = allColumns.length - 1
						else if (newColumnIndex >= allColumns.length) newColumnIndex = 0

						// Stop if we loop back to the original column
						if (newColumnIndex === originalColumnIndex) break
					}
				}

				if (foundEditableCell) {
					gridApi.stopEditing()
					gridApi.startEditingCell({
						rowIndex: newRowIndex,
						colKey: allColumns[newColumnIndex].getId(),
					})
					node?.setSelected(true, true)
				}
			},
			focusOnComponent() {
				this.$refs.comp?.onFocused ? this.$refs.comp.onFocused() : false
				this.$el.querySelector('.nk-control')?.focus() // Dropdowns
			},
			subscribeKeys() {
				// setTimeout(() => {
				this.$keyHandler.Subscribe({
					name: `table-arrow-left-${this.elementId}`,
					keyCode: [37, 13], // 37:arrow-left 13:enter
					altKey: false,
					shiftKey: false,
					el: document.getElementById(`${this.elementId}`),
					context: this,
					test: function (sender, e) {
						return true
						// return this.isEditable
					},
					handler: (sender, e) => {
						e.preventDefault()

						if (e.keyCode == 13 && e.ctrlKey) return true

						if (!this.gridApi) return false

						// this.gridApi.tabToNextCell()

						this.navigateEditableCell(this.gridApi as GridApi, 'left')


						return true
					},
				}) // table-arrow-left
				this.$keyHandler.Subscribe({
					name: `table-arrow-right-${this.elementId}`,
					keyCode: 39, // 39:arrow-right
					el: document.getElementById(`${this.elementId}`),
					context: this,
					test: function (sender, e) {
						return true
					},
					handler: (sender, e) => {
						e.preventDefault()

						if (!this.gridApi) return false

						// this.gridApi?.tabToPreviousCell()

						this.navigateEditableCell(this.gridApi as GridApi, 'right')

						return true

					},
				}) // table-arrow-right
				this.$keyHandler.Subscribe({
					name: `table-arrow-up-${this.elementId}`,
					keyCode: 38, // 38:arrow-up
					el: document.getElementById(`${this.elementId}`),
					context: this,
					test: function (sender, e) {
						return true
					},
					handler: (sender, e) => {
						e.preventDefault()

						if (!this.gridApi) return false

						this.navigateEditableCell(this.gridApi as GridApi, 'up')

						return true
					},
				}) // table-arrow-up
				this.$keyHandler.Subscribe({
					name: `table-arrow-down-${this.elementId}`,
					keyCode: 40, // 40:arrow-down
					ctrlKey: false,
					el: document.getElementById(`${this.elementId}`),
					context: this,
					handler: (sender, e) => {
						e.preventDefault()

						if (!this.gridApi) return false

						this.navigateEditableCell(this.gridApi as GridApi, 'down')

						return true
					},
				}) // table-arrow-down
				// }, 1)
			}
		},
	})
</script>

<style scoped></style>